import { useState, useRef, useCallback } from 'react';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { createReplayAdapter, ReplayAdapter } from '@/infrastructure/adapters/ReplayAdapter.js';
import type { MessageEvent, MessageInstance } from '@k-ucap/shared';

const convertToInstance = (event: MessageEvent): MessageInstance => ({
  id: event.id,
  messageType: event.messageType,
  standard: event.standard,
  name: event.name,
  source: event.source,
  target: event.target,
  direction: event.direction,
  timestamp: event.timestamp,
  fields: event.fields ?? event.payload ?? {},
  rawBytes: event.rawBytes,
  validation: event.validation,
  projectId: event.projectId,
  environment: event.environment,
  runId: event.runId,
  sequenceNumber: event.sequenceNumber,
});

export const useReplay = () => {
  const { messageEventRepository } = useRepositories();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const adapterRef = useRef<ReplayAdapter>(createReplayAdapter());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateStatus = useCallback(() => {
    const { current, total } = adapterRef.current.getProgress();
    setCurrentMessage(current);
    setTotalMessages(total);
    setProgress(total > 0 ? (current / total) * 100 : 0);
  }, []);

  const loadMessages = useCallback(
    async (runId: string) => {
      try {
        setError(null);
        adapterRef.current.stop();
        setIsPlaying(false);
        setIsPaused(false);
        setProgress(0);
        setCurrentMessage(0);
        const events = await messageEventRepository.findByRunId(runId);
        const messages = events
          .filter((e) => e.timestamp)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          .map(convertToInstance);
        adapterRef.current.loadMessages(messages);
        setTotalMessages(messages.length);
        return messages.length > 0;
      } catch (err) {
        setError((err as Error).message);
        return false;
      }
    },
    [messageEventRepository]
  );

  const play = useCallback(() => {
    if (adapterRef.current.getProgress().total === 0) return;
    adapterRef.current.setSpeed(speed);
    adapterRef.current.play();
    setIsPlaying(true);
    setIsPaused(false);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      updateStatus();
      const { current, total } = adapterRef.current.getProgress();
      if (current >= total) {
        setIsPlaying(false);
        setIsPaused(false);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    }, 100);
  }, [speed, updateStatus]);

  const pause = useCallback(() => {
    adapterRef.current.pause();
    setIsPaused(true);
    setIsPlaying(false);
  }, []);

  const resume = useCallback(() => {
    play();
  }, [play]);

  const stop = useCallback(() => {
    adapterRef.current.stop();
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentMessage(0);
    setProgress(0);
  }, []);

  const setPlaybackSpeed = useCallback((s: number) => {
    const next = Math.max(0.1, s);
    setSpeed(next);
    adapterRef.current.setSpeed(next);
  }, []);

  return {
    isPlaying,
    isPaused,
    progress,
    currentMessage,
    totalMessages,
    speed,
    error,
    loadMessages,
    play,
    pause,
    resume,
    stop,
    setPlaybackSpeed,
  };
};

export default useReplay;
